import { useState, useMemo } from "react";
import { View, TouchableOpacity } from "react-native";
import { Text, TouchableRipple, Searchbar } from "react-native-paper";
import { gql } from "@apollo/client";
import { Feather } from "@expo/vector-icons";
import Modal from "./Modal";
import { colors } from "../../theme";
import TextInput from "./TextInput";
import ModalHeader from "./Modal/ModalHeader";
import splitMatch from "../utils/splitMatch";
import { useIntercom } from "../hooks";
import config from "../config";
import useSearchAndSelect from "../hooks/useSearchAndSelect";

interface SharedProps {
  onChange(facility: Facility): void;
  value?: Facility;
  title: string;
  placeholder?: string;
}

interface FacilityPickerInputProps extends SharedProps {}

type FacilityPickerProps = SharedProps & {
  onDismiss(): void;
  description: string;
  visible: boolean;
};

export const SEARCH_FACILITY_QUERY = gql`
  query searchFacilities($term: String!) {
    searchFacilities(term: $term, limit: 10) {
      id
      name
      addresses {
        id
        address1
        city
        state
        postalCode
      }
    }
  }
`;

type Address = {
  id: string;
  address1: string;
  city: string;
  state?: string;
  postalCode?: string;
};

export type Facility = {
  id: string;
  name: string;
  addresses: Address[];
};

const buildAddress = (address: Address) => {
  const parts = [address.address1, address.city, address.state, address.postalCode];
  return parts.filter((part) => part).join(", ");
};

const SuggestionItem = (facility: Facility & { searchText: string; onPressSuggestion(facility: Facility): void }) => {
  const { name, addresses, searchText, onPressSuggestion } = facility;
  const segments = useMemo(() => splitMatch(name, searchText), [name, searchText]);

  return (
    <TouchableRipple onPress={() => onPressSuggestion(facility)}>
      <View className="flex-row items-center py-3 px-12">
        <View className="flex-1 pl-3">
          <Text>
            {segments.map(({ text, isHighlighted }, i) => (
              <Text
                key={i}
                numberOfLines={1}
                className={`text-regular-tight ${isHighlighted ? "font-700" : "font-400"} text-ink-darkest`}
              >
                {text}
              </Text>
            ))}
          </Text>
          {!!addresses && addresses.length > 0 && (
            <Text className="text-small-tight font-400 text-ink-lighter mt-2">{buildAddress(addresses[0])}</Text>
          )}
        </View>
      </View>
    </TouchableRipple>
  );
};

const ItemSeparatorComponent = () => <View className="border-b border-sky mx-6" />;

const renderItem =
  (searchText: string, onPressSuggestion: (facility: Facility) => void) =>
  ({ item: { id, name, addresses } }: { item: Facility }) => (
    <SuggestionItem
      key={id}
      {...{
        id,
        name,
        addresses,
        searchText,
        onPressSuggestion,
      }}
    />
  );

export function FacilityPicker({ onDismiss, description, visible, onChange, title, placeholder }: FacilityPickerProps) {
  const {
    searchText,
    onSearchTextChange,
    items: suggestionList,
    loading,
    showNoResultsFound,
  } = useSearchAndSelect<Facility>(SEARCH_FACILITY_QUERY);

  const intercom = useIntercom();

  const onPressSuggestion = (facility: Facility) => onChange(facility);

  const ListFooterComponent = () =>
    searchText ? (
      <View className="flex-row justify-center items-center py-4">
        <TouchableOpacity
          onPress={() => {
            intercom.present(config.intercomContent.cantFindFacility);
          }}
        >
          <View className="flex-row items-center">
            <Feather name="help-circle" size={24} color={colors.ink.darkest} />
            <Text className="text-center text-regular-tight ml-2">Can't find your facility?</Text>
          </View>
        </TouchableOpacity>
      </View>
    ) : null;

  const HeaderComponent = (
    <>
      <ModalHeader {...{ title, hasBorder: true, onDismiss }} />
      <View className="p-6 bg-white">
        <Text className="text-small-normal mb-8 font-400 text-ink-dark">{description}</Text>
        <Searchbar
          autoFocus
          elevation={0}
          placeholderTextColor={colors.ink.light}
          style={{
            backgroundColor: "white",
            borderColor: colors.sky.dark,
            borderRadius: 8,
            borderWidth: 1,
          }}
          {...{ loading, onChangeText: onSearchTextChange, placeholder }}
          value={searchText}
        />
        {showNoResultsFound && !!searchText ? (
          <View className="justify-center items-center mt-4">
            <Text className="text-ink-light text-regular-tight">No results found.</Text>
          </View>
        ) : null}
      </View>
    </>
  );

  return (
    <Modal
      fullScreen
      webStyle={{ height: "75vh" }}
      flatListProps={{
        ItemSeparatorComponent,
        renderItem: renderItem(searchText, onPressSuggestion),
        ListFooterComponent: ListFooterComponent,
        data: suggestionList,
        extraData: searchText,
      }}
      visible={visible}
      {...{
        HeaderComponent,
        ...{ title, hasBorder: true, onDismiss },
      }}
    />
  );
}

const FacilityPickerInput = ({ onChange, value, title, placeholder }: FacilityPickerInputProps) => {
  const [showModal, setShowModal] = useState(false);

  function hideModal() {
    setShowModal(false);
  }

  return (
    <TouchableOpacity activeOpacity={0.7} onPress={() => setShowModal(true)}>
      <TextInput editable={false} pointerEvents="none" value={value ? value.name : ""} placeholder={placeholder} />

      <FacilityPicker
        onDismiss={hideModal}
        description="Select the facility where you are employed."
        visible={showModal}
        onChange={(facility) => {
          onChange(facility);
          if (facility) hideModal();
        }}
        title={title}
        placeholder={placeholder}
        value={value}
      />
    </TouchableOpacity>
  );
};

export default FacilityPickerInput;
